import { ITAAS_PORT_REGISTRY } from "./registry.js";
const HOST = ITAAS_PORT_REGISTRY.host;
function findEntry(product, role) {
    const products = ITAAS_PORT_REGISTRY.products;
    return products[product]?.[role];
}
/**
 * Port numérique pour un produit + rôle.
 * @throws si l'entrée n'existe pas
 */
export function getPort(product, role) {
    const entry = findEntry(product, role);
    if (!entry) {
        throw new Error(`@itaas/ports: entrée inconnue ${product}.${role}`);
    }
    return entry.port;
}
/**
 * URL locale http://localhost:{port}{basePath}
 */
export function localServiceUrl(product, role, options) {
    const entry = findEntry(product, role);
    if (!entry) {
        throw new Error(`@itaas/ports: entrée inconnue ${product}.${role}`);
    }
    const protocol = options?.protocol ?? "http";
    const host = options?.host ?? HOST;
    const base = entry.basePath ?? "";
    return `${protocol}://${host}:${entry.port}${base}`;
}
/** Variable d'environnement recommandée pour l'URL publique du service (front). */
export function getUrlEnvVar(product, role) {
    return findEntry(product, role)?.urlEnvVar;
}
/** Variables d'environnement pour le port du service. */
export function getPortEnvVars(product, role) {
    const entry = findEntry(product, role);
    if (!entry) {
        throw new Error(`@itaas/ports: entrée inconnue ${product}.${role}`);
    }
    return entry.envVars;
}
/** Ligne .env exemple : API_PORT=3020 */
export function envLine(product, role) {
    const entry = findEntry(product, role);
    if (!entry) {
        throw new Error(`@itaas/ports: entrée inconnue ${product}.${role}`);
    }
    const primary = entry.envVars[0] ?? "PORT";
    return `${primary}=${entry.port}`;
}
/** Origine HTTP pour proxy Vite / fetch serveur (sans chemin API). */
export function originAtPort(port, host = "127.0.0.1") {
    return `http://${host}:${port}`;
}
export function localOrigin(product, role) {
    return originAtPort(getPort(product, role));
}
/** Constantes nommées pour imports directs (évite les magic numbers). */
export const PORTS = {
    SUITE_WEB: getPort("suite", "web"),
    ERP_API: getPort("erp", "api"),
    ERP_WEB: getPort("erp", "web"),
    INVENTORY_API: getPort("inventory", "api"),
    INVENTORY_WEB: getPort("inventory", "web"),
    PRODUCT_API: getPort("product", "api"),
    PRODUCT_WEB: getPort("product", "web"),
    IAM_API: getPort("iam", "api"),
    NOTIFICATIONS_API: getPort("notifications", "api"),
    SECURITY_WEB: getPort("security", "web"),
    RH_WEB: getPort("rh", "web"),
    BIOMETRIC_GATEWAY_API: getPort("biometricGateway", "api"),
    BILLING_API: getPort("billing", "api"),
    BILLING_WEB: getPort("billing", "web"),
    HOSPITOL_API: getPort("hospitol", "api"),
    HOSPITOL_WEB: getPort("hospitol", "web"),
    /** @deprecated Utiliser HOSPITOL_* */
    PHARMACY_API: getPort("hospitol", "api"),
    PHARMACY_WEB: getPort("hospitol", "web"),
    LEGAL_API: getPort("legal", "api"),
    LEGAL_WEB: getPort("legal", "web"),
    EDU_API: getPort("edu", "api"),
    EDU_WEB: getPort("edu", "web"),
    MINEPORTAL_API: getPort("mineportal", "api"),
    MINEPORTAL_WEB: getPort("mineportal", "web"),
    AUTOID_API: getPort("autoid", "api"),
    PEP_API: getPort("pep", "api"),
    PEP_WEB: getPort("pep", "web"),
    SUBPORTAL_API: getPort("subportal", "api"),
    SUBPORTAL_WEB: getPort("subportal", "web"),
    COMEX_API: getPort("comex", "api"),
    COMEX_WEB: getPort("comex", "web"),
    CRM_API: getPort("crm", "api"),
    CRM_WEB: getPort("crm", "web"),
    FINANCE_API: getPort("finance", "api"),
    FINANCE_WEB: getPort("finance", "web"),
    PAYMENTS_API: getPort("payments", "api"),
    PAYMENTS_WEB: getPort("payments", "web"),
    PROCUREMENT_WEB: getPort("procurement", "web"),
    INVENTORY_KANAKKU_WEB: getPort("inventoryKanakku", "web"),
    PRODUCT_KANAKKU_WEB: getPort("productKanakku", "web"),
    WORKFORCE_WEB: getPort("workforce", "web"),
    RECRUITMENT_WEB: getPort("recruitment", "web"),
    ATTENDANCE_WEB: getPort("attendance", "web"),
    INSURANCE_WEB: getPort("insurance", "web"),
    INSURANCE_API: getPort("insurance", "api"),
};
export const URLS = {
    SUITE_WEB: localServiceUrl("suite", "web"),
    ERP_API: localServiceUrl("erp", "api"),
    ERP_WEB: localServiceUrl("erp", "web"),
    INVENTORY_API: localServiceUrl("inventory", "api"),
    INVENTORY_WEB: localServiceUrl("inventory", "web"),
    PRODUCT_API: localServiceUrl("product", "api"),
    PRODUCT_WEB: localServiceUrl("product", "web"),
    IAM_API: localServiceUrl("iam", "api"),
    NOTIFICATIONS_API: localServiceUrl("notifications", "api"),
    SECURITY_WEB: localServiceUrl("security", "web"),
    RH_WEB: localServiceUrl("rh", "web"),
    BIOMETRIC_GATEWAY_API: localServiceUrl("biometricGateway", "api"),
    BILLING_API: localServiceUrl("billing", "api"),
    BILLING_WEB: localServiceUrl("billing", "web"),
    HOSPITOL_API: localServiceUrl("hospitol", "api"),
    HOSPITOL_WEB: localServiceUrl("hospitol", "web"),
    /** @deprecated Utiliser HOSPITOL_* */
    PHARMACY_API: localServiceUrl("hospitol", "api"),
    PHARMACY_WEB: localServiceUrl("hospitol", "web"),
    LEGAL_API: localServiceUrl("legal", "api"),
    LEGAL_WEB: localServiceUrl("legal", "web"),
    EDU_API: localServiceUrl("edu", "api"),
    EDU_WEB: localServiceUrl("edu", "web"),
    MINEPORTAL_API: localServiceUrl("mineportal", "api"),
    MINEPORTAL_WEB: localServiceUrl("mineportal", "web"),
    AUTOID_API: localServiceUrl("autoid", "api"),
    AUTOID_WEB: localServiceUrl("autoid", "api"),
    PEP_API: localServiceUrl("pep", "api"),
    PEP_WEB: localServiceUrl("pep", "web"),
    SUBPORTAL_API: localServiceUrl("subportal", "api"),
    SUBPORTAL_WEB: localServiceUrl("subportal", "web"),
    COMEX_API: localServiceUrl("comex", "api"),
    COMEX_WEB: localServiceUrl("comex", "web"),
    CRM_API: localServiceUrl("crm", "api"),
    CRM_WEB: localServiceUrl("crm", "web"),
    FINANCE_API: localServiceUrl("finance", "api"),
    FINANCE_WEB: localServiceUrl("finance", "web"),
    PAYMENTS_API: localServiceUrl("payments", "api"),
    PAYMENTS_WEB: localServiceUrl("payments", "web"),
    PROCUREMENT_WEB: localServiceUrl("procurement", "web"),
    INVENTORY_KANAKKU_WEB: localServiceUrl("inventoryKanakku", "web"),
    PRODUCT_KANAKKU_WEB: localServiceUrl("productKanakku", "web"),
    WORKFORCE_WEB: localServiceUrl("workforce", "web"),
    RECRUITMENT_WEB: localServiceUrl("recruitment", "web"),
    ATTENDANCE_WEB: localServiceUrl("attendance", "web"),
    INSURANCE_WEB: localServiceUrl("insurance", "web"),
    INSURANCE_API: localServiceUrl("insurance", "api"),
};
/** Origine web sans chemin API (handoff Suite). */
export function moduleWebOrigin(product) {
    const url = localServiceUrl(product, "web");
    return url.replace(/\/api\/v1$/, "").replace(/\/api$/, "");
}
